import React, { useEffect, useState } from 'react';
import { useLazyGetSnapshotCampaignQuery } from '../../../store/api/campaign.api';
import { useAppSelector } from '../../../store/store';
import { getCurrentlyWorkingSession } from '../../../store/selector/session.selector';
import { SessionStage } from '../../../interfaces/session.interface';
import ViewDeliverableSection from './view/ViewDeliverableSection';
import { Spinner } from 'react-bootstrap';
import { ICampaignDetails } from '../../../interfaces/campaign.interface';
import { useGetOrganizationTemplatesQuery } from '../../../store/api/template.api';
import { getOrganizationId } from '../../../store/selector/organization.selector';
import { setTemplateDetailsInCampaign } from '../../../utils/campaign.utils';

const PlanView = () => {
    const currentlyWorkingSession = useAppSelector(getCurrentlyWorkingSession);
    const organizationId = useAppSelector(getOrganizationId);
    const [snapshotCampaign, setSnapshotCampaign] = useState<ICampaignDetails | null>(null);
    const [isFetching, setIsFetching] = useState(false);
    const [fetchSnapshotCampaign] = useLazyGetSnapshotCampaignQuery();
    const { data: organizationTemplates } = useGetOrganizationTemplatesQuery(
        organizationId ? organizationId : 0,
        {
            skip: !organizationId,
        },
    );

    const [isCampaignDetailsCollapsed, setIsCampaignDetailsCollapsed] = useState(true);

    const toggleCampaignDetailsCollapsed = () => {
        setIsCampaignDetailsCollapsed(!isCampaignDetailsCollapsed);
    };

    useEffect(() => {
        const fetchSnapshotCampaignData = async () => {
            if (currentlyWorkingSession && currentlyWorkingSession.stage !== SessionStage.PLAN) {
                setIsFetching(true);
                const response = await fetchSnapshotCampaign(currentlyWorkingSession.sessionKey);
                const data = response.data;
                if (data) {
                    const updatedCampaign = setTemplateDetailsInCampaign(
                        data,
                        organizationTemplates ? organizationTemplates : [],
                    );
                    setSnapshotCampaign(updatedCampaign);
                }
                setIsFetching(false);
            }
        };

        fetchSnapshotCampaignData();
    }, [currentlyWorkingSession, fetchSnapshotCampaign]);

    return (
        <div className="tw-w-full">
            {isFetching && (
                <div className="tw-w-full tw-mt-5 tw-flex tw-flex-col tw-items-center tw-gap-y-2">
                    <Spinner className="tw-text-purple-100" />
                    <div>Fetching campaign details...</div>
                </div>
            )}
            {snapshotCampaign && !isFetching && (
                <>
                    <div className="tw-w-full tw-flex tw-items-center tw-justify-between tw-mb-6">
                        <div className="tw-flex tw-items-center tw-gap-x-6">
                            <div className="tw-flex tw-items-center tw-gap-x-2">
                                <div className="tw-font-medium tw-cursor-pointer tw-text-lg">
                                    {snapshotCampaign.name}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="tw-w-full tw-rounded-sm tw-py-3 tw-px-4 tw-border tw-border-blue-50 tw-shadow-sm">
                        <div
                            className="tw-w-full tw-flex tw-items-center tw-justify-between tw-cursor-pointer"
                            onClick={toggleCampaignDetailsCollapsed}
                        >
                            <div className="tw-flex tw-items-center tw-gap-x-3">
                                <i className="ph-bold ph-faders-horizontal lg"></i>
                                <span className="tw-text-base">Campaign Details</span>
                            </div>
                            {isCampaignDetailsCollapsed ? (
                                <i className="ph-bold ph-caret-down lg tw-cursor-pointer"></i>
                            ) : (
                                <i className="ph-bold ph-caret-up lg tw-cursor-pointer"></i>
                            )}
                        </div>
                        {!isCampaignDetailsCollapsed && (
                            <div className="tw-w-full tw-mt-6 tw-pl-4">
                                <div className="tw-w-full tw-mt-6 tw-flex tw-items-start tw-gap-x-2 tw-text-base">
                                    <span className="tw-font-semibold tw-whitespace-nowrap">
                                        Objective:
                                    </span>
                                    <span className="text-justify tw-w-full">
                                        {snapshotCampaign.objective}
                                    </span>
                                </div>
                                <div className="tw-w-full tw-mt-2 tw-flex tw-items-start tw-gap-x-2 tw-text-base">
                                    <span className="tw-font-semibold tw-whitespace-nowrap">
                                        Target Audience:
                                    </span>
                                    <p className="text-justify tw-w-full">
                                        {snapshotCampaign.target_audience}
                                    </p>
                                </div>
                                <div className="tw-w-full tw-mt-2 tw-flex tw-items-start tw-gap-x-2 tw-text-base">
                                    <span className="tw-font-semibold tw-whitespace-nowrap">
                                        Key Message:
                                    </span>
                                    <p className="text-justify tw-w-full">
                                        {snapshotCampaign.key_message}
                                    </p>
                                </div>
                                <div className="tw-w-full tw-mt-2 tw-flex tw-items-start tw-gap-x-2 tw-text-base">
                                    <span className="tw-font-semibold tw-whitespace-nowrap">
                                        Other Guidance:
                                    </span>
                                    <p className="text-justify tw-w-full">
                                        {snapshotCampaign.other_details}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <ViewDeliverableSection deliverables={snapshotCampaign.deliverables} />
                </>
            )}
            {!snapshotCampaign && !isFetching && (
                <div className="tw-w-full tw-mt-5 tw-flex tw-flex-col tw-items-center tw-gap-y-2">
                    <div>There was an error in the campaign. Please try another campaign</div>
                </div>
            )}
        </div>
    );
};

export default PlanView;
